import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StorageService, storageStore, StorageType } from '@app/services/storage';
import { Endpoint } from '@services/endpoints/';
import { Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { SystemAlertsService } from '../system-alerts';
import { IBrsToken, BrsTokenServiceKey } from './_models';
import { I18nService } from '@src/app/i18n/services';

@Injectable({
	providedIn: 'root'
})
export class BrsTokenService {
	private set brsToken(token: string) {
		this.storageService.setItem(StorageType.cookie, BrsTokenServiceKey.brsToken, token, storageStore);
	}

	private get brsToken(): string {
		return this.storageService.getItem(StorageType.cookie, BrsTokenServiceKey.brsToken, storageStore);
	}

	private set brsTokenExpiry(expires: number) {
		this.storageService.setItem(StorageType.cookie, BrsTokenServiceKey.brsTokenExpiry, String(expires), storageStore);
	}

	private get brsTokenExpiry(): number {
		return Number(this.storageService.getItem(StorageType.cookie, BrsTokenServiceKey.brsTokenExpiry, storageStore));
	}

	public get brsTokenExpired(): boolean {
		if (!this.brsTokenExpiry) return true;

		// Get current time in seconds, minus 10 to ensure we don't have tokens expiring during the HTTP request
		const secondsSinceEpoch = Date.now() / 1000 - 10;

		return this.brsTokenExpiry <= secondsSinceEpoch;
	}

	constructor(
		private http: HttpClient,
		private i18nService: I18nService,
		private storageService: StorageService,
		private systemAlertsService: SystemAlertsService
	) {}

	public getBrsToken(): Observable<string> {
		return new Observable((observer) => {
			if (!this.brsTokenExpired) {
				observer.next(this.brsToken);
				observer.complete();
			} else {
				this.refreshBrsToken().subscribe(
					() => {
						observer.next(this.brsToken);
						observer.complete();
					},
					() => {
						this.systemAlertsService.removeAll();
						observer.error();
						observer.complete();
					}
				)
			}
		});
	}

	public extractBrsToken(result: any) {
		this.brsToken = result['token'];
		this.brsTokenExpiry = result['tokenExp'] / 1000;
	}

	public refreshBrsToken(): Observable<string> {
		const url = `${environment.brsAuthentication}${Endpoint.brsJwtToken}`;

		return this.http.get<any>(url).pipe(
			tap((result: any) => {
				if (result) {
					this.extractBrsToken(result);
				} else {
					throwError({
						errorMessage: this.i18nService.get('Oops! Something went wrong.', 'app.api.response.general-request-error')
					});
				}
			})
		);
	}
}
